import moment, { Moment } from 'moment-timezone'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getAnalyticsDefaultPeriod } from '@/common/utils/analytics_period'
import { TCampaign } from '@/typings/api/campaigns.type'

import { getCampaign } from '../api/getCampaign'
import {
  getInstagramInstantwinCampaignAnalytics,
  InstagramInstantwinCampaignAnalyticsApiError
} from '../api/getInstagramInstantwinCampaignAnalytics'
import {
  getInstagramInstantwinEnqueteAnalytics,
  InstagramInstantwinEnqueteAnalyticsApiError
} from '../api/getInstagramInstantwinEnqueteAnalytics'
import {
  getInstagramInstantwinMessageSendCounts,
  InstagramInstantwinMessageSendCountsApiError
} from '../api/getInstagramInstantwinMessageSendCounts'
import { getInstagramInstantwinPrizes, InstagramInstantwinPrizesApiError } from '../api/getInstagramInstantwinPrizes'
import { putCampaignPublicFlags, PutCampaignPublicFlagsApiError } from '../api/putCampaignPublicFlags'
import {
  AnalyticsSeries,
  ErrorCode,
  InstagramInstantwinAnalyticsRow,
  InstagramInstantwinEnqueteAnswer,
  InstagramInstantwinEnqueteQuestion,
  InstagramInstantwinMessageSendCountRow,
  Interval,
  MessageSendCountSortBy,
  MetricSelection,
  PrizeOption,
  TimeRange
} from '../types'

type MetricValueKey = 'applicant_count' | 'entry_count' | 'new_applicant_count' | 'winner_count' | 'follower_count'

type DateRangeValue = [] | [string | Date | Moment, string | Date | Moment]

type CampaignMeta = {
  name: string | null
  status: number | null
  pictureUrl: string | null
  iconType: 'instagram'
  endDatetime: string | null
  hash: string
}

const createEmptySeries = (): AnalyticsSeries => ({
  applicant: [],
  follower: [],
  entry: [],
  newApplicant: [],
  winner: []
})

const buildTimeRange = (rows: InstagramInstantwinAnalyticsRow[], interval: Interval): TimeRange[] => {
  if (interval === 'hour') {
    return []
  }

  const timeRange: TimeRange[] = []

  if (!rows.length) {
    return timeRange
  }

  const addNumber = Number(interval.match(/\d+/g))

  let timeEnd: number | undefined
  let timeStart = moment(moment(rows[0].datetime).format('YYYY-MM-DD 00:00:00')).unix()
  const endDateUnix = moment(moment(rows[rows.length - 1].datetime).format('YYYY-MM-DD HH:mm:00')).unix()

  while (timeStart < endDateUnix) {
    if (interval === '1_day' || interval === '7_day') {
      // 日数のunix値: 日数 x 一日のunix値 (一日のunix値: 86400)
      timeEnd = timeStart + addNumber * 86400 - 1
    } else if (interval === '1_month') {
      timeEnd = moment.unix(timeStart).add(addNumber, 'months').startOf('month').unix() - 1
    }

    if (typeof timeEnd === 'number' && timeEnd > endDateUnix) {
      timeEnd = endDateUnix
    }

    if (typeof timeEnd !== 'number') {
      break
    }

    timeRange.push({
      start: timeStart,
      end: timeEnd
    })

    timeStart = timeEnd + 1
  }

  if (timeRange.length) {
    timeRange[0].start = moment(rows[0].datetime).unix()
  } else {
    timeRange.push({ start: endDateUnix, end: endDateUnix })
  }

  return timeRange
}

const getMetricValue = (insight: InstagramInstantwinAnalyticsRow, metric: MetricValueKey): number | null => {
  return isNaN(Number(insight[metric])) ? null : insight[metric]
}

const getMetricArray = (insights: InstagramInstantwinAnalyticsRow[], metric: MetricValueKey): Array<number | null> => {
  return insights.map(insight => getMetricValue(insight, metric))
}

const toNumber = (value: number | null): number => {
  return value !== null && !isNaN(value) ? value : 0
}

const addition = (numbers: Array<number | null> = []): number | null => {
  const values = numbers.filter(value => value !== null)

  if (!values.length) {
    return null
  }

  return numbers.map(value => toNumber(value)).reduce((a, c) => a + c, 0)
}

const getFirstValueExceptNull = (analytics: InstagramInstantwinAnalyticsRow[], value: MetricValueKey): number => {
  const data = analytics.find(analytic => analytic[value] !== null)

  if (!data) {
    return 0
  }

  return data[value] ?? 0
}

const getLastValue = (analytics: InstagramInstantwinAnalyticsRow[], value: MetricValueKey): number | null => {
  const data = analytics.filter(analytic => analytic[value] !== null)

  if (data.length === 0) {
    return null
  }

  return data[data.length - 1][value] ?? null
}

const getValueByPeriod = (
  analytics: InstagramInstantwinAnalyticsRow[],
  value: MetricValueKey,
  fullLength: number,
  firstDatetime: string
): number | null => {
  const data = analytics.filter(analytic => analytic[value] !== null)

  if (data.length === 0) {
    return null
  }

  // If there is only one period, return last value
  if (analytics.length === fullLength) {
    return data[data.length - 1][value] ?? null
  }

  // If this is the first period, return first value
  if (analytics[0] && moment(analytics[0].datetime).isSame(firstDatetime)) {
    return data[0][value] ?? null
  }

  return data[data.length - 1][value] ?? null
}

const getValueByHour = (
  analytics: InstagramInstantwinAnalyticsRow[],
  datetime: string,
  value: MetricValueKey
): number | null => {
  const data = analytics.find(analytic => moment(analytic.datetime).isSame(datetime, 'hour'))

  if (!data) {
    return null
  }

  return data[value] ?? null
}

export const useInstagramInstantwinCampaignAnalyticsStore = defineStore(
  'InstagramInstantwinCampaignAnalyticsStore',
  () => {
    const campaignId = ref<number | null>(null)
    const campaign = ref<TCampaign | null>(null)
    const analyticsRows = ref<InstagramInstantwinAnalyticsRow[]>([])
    const prizeOptions = ref<PrizeOption[]>([{ id: null, name: '全ての応募グループ' }])
    const prizeId = ref<number | null>(null)
    const enqueteQuestions = ref<InstagramInstantwinEnqueteQuestion[]>([])
    const enqueteSelectedId = ref<number | null>(null)
    const enqueteAnswers = ref<InstagramInstantwinEnqueteAnswer[]>([])
    const enqueteTotalCount = ref(0)
    const messageSendCountRows = ref<InstagramInstantwinMessageSendCountRow[]>([])
    const messageSendCountTotal = ref(0)
    const messageSendCountPagingNo = ref(1)
    const messageSendCountLimitCount = ref(25)
    const messageSendCountSortBy = ref<MessageSendCountSortBy | ''>('')
    const enqueteLoading = ref(false)
    const messageSendCountLoading = ref(false)
    const dateTimeRange = ref<DateRangeValue>([])
    const defaultDateTimeRange = ref<DateRangeValue>([])
    const interval = ref<Interval>('1_day')
    const value1 = ref<MetricSelection>('follower')
    const value2 = ref<MetricSelection>('applicant')
    const isBeginAtZero = ref(false)
    const isPublicAnalyticsPage = ref(0)
    const isPublicAnalyticsApi = ref(0)
    const loading = ref(false)
    const errorCode = ref<ErrorCode | null>(null)
    const errorMessage = ref<string | null>(null)

    const campaignMeta = computed<CampaignMeta>(() => ({
      name: campaign.value?.title ?? null,
      status: campaign.value?.status ?? null,
      pictureUrl: campaign.value?.account?.pictureUrl ?? null,
      iconType: 'instagram',
      endDatetime: campaign.value?.endDatetime ?? null,
      hash: campaign.value?.hash ?? ''
    }))

    const timeRange = computed(() => buildTimeRange(analyticsRows.value, interval.value))
    const timeRangeForLabel = computed(() => buildTimeRange(analyticsRows.value, interval.value))

    const analytics = computed<AnalyticsSeries>(() => {
      const data = createEmptySeries()

      if (!analyticsRows.value.length) {
        return data
      }

      if (interval.value === 'hour') {
        const lastIndex = analyticsRows.value.length - 1

        analyticsRows.value.forEach((value, index) => {
          if (index !== lastIndex) {
            data.follower.push(value.follower_count)
            data.applicant.push(value.applicant_count)
            data.entry.push(value.entry_count)
            data.newApplicant.push(value.new_applicant_count)
            data.winner.push(value.winner_count)
          }
        })

        return data
      }

      const endDateUnix = moment(analyticsRows.value[analyticsRows.value.length - 1].datetime)
        .startOf('minute')
        .unix()

      timeRange.value.forEach((time, index) => {
        // 該当する期間の分析データを取得
        let slice = analyticsRows.value
          .filter(analytic => {
            return moment(analytic.datetime).unix() >= time.start && moment(analytic.datetime).unix() <= time.end
          })
          .map(analytic => analytic)

        if (interval.value === '1_day') {
          const momentDate = index < timeRange.value.length - 1 ? moment.unix(time.start) : moment.unix(time.end)
          const datetime = momentDate.startOf('hour').format('YYYY-MM-DD HH:mm')

          data.follower.push(getValueByHour(slice, datetime, 'follower_count'))
        } else {
          data.follower.push(
            getValueByPeriod(slice, 'follower_count', analyticsRows.value.length, analyticsRows.value[0].datetime)
          )
        }

        if (time.end === endDateUnix) {
          slice = slice.filter(value => moment(value.datetime).unix() !== endDateUnix)
        }

        data.applicant.push(addition(getMetricArray(slice, 'applicant_count')))
        data.entry.push(addition(getMetricArray(slice, 'entry_count')))
        data.newApplicant.push(addition(getMetricArray(slice, 'new_applicant_count')))
        data.winner.push(addition(getMetricArray(slice, 'winner_count')))
      })

      return data
    })

    const labels = computed(() => {
      if (!analyticsRows.value.length) {
        return []
      }

      if (interval.value === 'hour') {
        return analyticsRows.value.slice(0, -1).map(value => {
          return moment(value.datetime).startOf('hour').format('YYYY/MM/DD HH:mm')
        })
      }

      if (interval.value === '1_day') {
        return timeRangeForLabel.value.map((time, index) => {
          if (index === timeRangeForLabel.value.length - 1) {
            return moment.unix(time.end).startOf('hour').format('YYYY/MM/DD')
          }

          return moment.unix(time.start).startOf('hour').format('YYYY/MM/DD')
        })
      }

      if (interval.value === '7_day') {
        return timeRangeForLabel.value.map(time => {
          return `${moment.unix(time.start).format('MM/DD')}-${moment.unix(time.end).format('MM/DD')}`
        })
      }

      if (interval.value === '1_month') {
        return timeRangeForLabel.value.map(time => {
          return moment.unix(time.start).format('YYYY/MM')
        })
      }

      return []
    })

    const entryCount = computed(() => {
      if (!analyticsRows.value.length) {
        return null
      }

      const columnData = analyticsRows.value.slice(0, analyticsRows.value.length - 1)
      return addition(getMetricArray(columnData, 'entry_count'))
    })

    const applicantCount = computed(() => {
      if (!analyticsRows.value.length) {
        return null
      }

      const columnData = analyticsRows.value.slice(0, analyticsRows.value.length - 1)
      return addition(getMetricArray(columnData, 'applicant_count'))
    })

    const newApplicantCount = computed(() => {
      if (!analyticsRows.value.length) {
        return null
      }

      const columnData = analyticsRows.value.slice(0, analyticsRows.value.length - 1)
      return addition(getMetricArray(columnData, 'new_applicant_count'))
    })

    const winnerCount = computed(() => {
      if (!analyticsRows.value.length) {
        return null
      }

      const columnData = analyticsRows.value.slice(0, analyticsRows.value.length - 1)
      return addition(getMetricArray(columnData, 'winner_count'))
    })

    const followerUpDown = computed(() => {
      if (!analyticsRows.value.length) {
        return 0
      }

      const first = getFirstValueExceptNull(analyticsRows.value, 'follower_count')
      const last = getLastValue(analyticsRows.value, 'follower_count')

      if (last === null) {
        return 0
      }

      return last - first
    })

    const setError = (code: ErrorCode | null, message?: string) => {
      errorCode.value = code
      errorMessage.value = message ?? null
    }

    const setDateTimeRange = (value?: DateRangeValue | null) => {
      if (!value || value.length === 0) {
        dateTimeRange.value = defaultDateTimeRange.value
        return
      }

      dateTimeRange.value = value
    }

    const resetEnqueteState = () => {
      enqueteQuestions.value = []
      enqueteSelectedId.value = null
      enqueteAnswers.value = []
      enqueteTotalCount.value = 0
    }

    const resetMessageSendCountState = () => {
      messageSendCountRows.value = []
      messageSendCountTotal.value = 0
      messageSendCountPagingNo.value = 1
      messageSendCountSortBy.value = ''
    }

    const resetAdditionalAnalytics = () => {
      resetEnqueteState()
      resetMessageSendCountState()
    }

    const reset = () => {
      campaignId.value = null
      campaign.value = null
      analyticsRows.value = []
      prizeOptions.value = [{ id: null, name: '全ての応募グループ' }]
      prizeId.value = null
      resetAdditionalAnalytics()
      messageSendCountLimitCount.value = 25
      enqueteLoading.value = false
      messageSendCountLoading.value = false
      dateTimeRange.value = []
      defaultDateTimeRange.value = []
      interval.value = '1_day'
      value1.value = 'follower'
      value2.value = 'applicant'
      isBeginAtZero.value = false
      isPublicAnalyticsPage.value = 0
      isPublicAnalyticsApi.value = 0
      loading.value = false
      setError(null)
    }

    const applyCampaignDateRange = (target: TCampaign) => {
      const range: DateRangeValue = getAnalyticsDefaultPeriod(target.startDatetime, target.endDatetime)
      dateTimeRange.value = range
      defaultDateTimeRange.value = range
    }

    const fetchCampaign = async (id: number): Promise<boolean> => {
      try {
        const response = await getCampaign(id)
        campaign.value = response
        campaignId.value = response.id
        isPublicAnalyticsPage.value = response.isPublicAnalyticsPage ?? 0
        isPublicAnalyticsApi.value = response.isPublicAnalyticsApi ?? 0
        applyCampaignDateRange(response)
        setError(null)
        return true
      } catch {
        setError('UNKNOWN', 'キャンペーン情報の取得に失敗しました。')
        return false
      }
    }

    const fetchAnalytics = async (): Promise<boolean> => {
      if (!campaignId.value) {
        setError('UNKNOWN', 'campaignId is required')
        return false
      }

      const [startRaw, endRaw] = dateTimeRange.value
      const params = {
        campaignId: campaignId.value,
        prizeId: prizeId.value ?? undefined,
        startDatetime: startRaw ? moment(startRaw).format('YYYY-MM-DD') : undefined,
        endDatetime: endRaw ? moment(endRaw).format('YYYY-MM-DD') : undefined
      }

      try {
        analyticsRows.value = await getInstagramInstantwinCampaignAnalytics(params)
        setError(null)
        return true
      } catch (error) {
        if (error instanceof InstagramInstantwinCampaignAnalyticsApiError) {
          setError(error.type as ErrorCode, error.message)
        } else {
          setError('UNKNOWN', 'キャンペーン分析の取得に失敗しました。')
        }
        return false
      }
    }

    const fetchEnqueteAnalytics = async (): Promise<boolean> => {
      if (!campaignId.value) {
        setError('UNKNOWN', 'campaignId is required')
        return false
      }
      if (!prizeId.value) {
        resetEnqueteState()
        return false
      }

      const [startRaw, endRaw] = dateTimeRange.value
      const params = {
        campaignId: campaignId.value,
        prizeId: prizeId.value,
        startDatetime: startRaw ? moment(startRaw).format('YYYY-MM-DD') : '',
        endDatetime: endRaw ? moment(endRaw).format('YYYY-MM-DD') : '',
        selectNodeId: enqueteSelectedId.value ?? undefined
      }

      enqueteLoading.value = true
      try {
        const payload = await getInstagramInstantwinEnqueteAnalytics(params)
        enqueteQuestions.value = payload.questions
        enqueteSelectedId.value = payload.selectedQuestion?.id ?? null
        enqueteAnswers.value = payload.answers
        enqueteTotalCount.value = payload.totalCount
        setError(null)
        return true
      } catch (error) {
        if (error instanceof InstagramInstantwinEnqueteAnalyticsApiError) {
          setError(error.type as ErrorCode, error.message)
        } else {
          setError('UNKNOWN', 'アンケート分析の取得に失敗しました。')
        }
        return false
      } finally {
        enqueteLoading.value = false
      }
    }

    const fetchMessageSendCounts = async (forCsv = false) => {
      if (!campaignId.value) {
        setError('UNKNOWN', 'campaignId is required')
        return null
      }
      if (!prizeId.value) {
        resetMessageSendCountState()
        return null
      }

      const [startRaw, endRaw] = dateTimeRange.value
      const params = {
        campaignId: campaignId.value,
        prizeId: prizeId.value,
        startDatetime: startRaw ? moment(startRaw).format('YYYY-MM-DD') : '',
        endDatetime: endRaw ? moment(endRaw).format('YYYY-MM-DD') : '',
        sortBy: messageSendCountSortBy.value || undefined,
        pagingNo: messageSendCountPagingNo.value,
        limitCount: messageSendCountLimitCount.value,
        forCsv
      }

      if (!forCsv) {
        messageSendCountLoading.value = true
      }
      try {
        const payload = await getInstagramInstantwinMessageSendCounts(params)
        if (!forCsv) {
          messageSendCountRows.value = payload.rows
          messageSendCountTotal.value = payload.dataCount
          setError(null)
        }
        return payload
      } catch (error) {
        if (error instanceof InstagramInstantwinMessageSendCountsApiError) {
          setError(error.type as ErrorCode, error.message)
        } else {
          setError('UNKNOWN', '送信数分析の取得に失敗しました。')
        }
        return null
      } finally {
        if (!forCsv) {
          messageSendCountLoading.value = false
        }
      }
    }

    const fetchPrizes = async () => {
      if (!campaignId.value) {
        return
      }

      try {
        const prizes = await getInstagramInstantwinPrizes({ campaignId: campaignId.value, forCsv: 1 })
        if (prizes.length === 1) {
          prizeOptions.value = prizes
          setPrizeId(prizes[0].id)
        } else {
          prizeOptions.value = [{ id: null, name: '全ての応募グループ' }, ...prizes]
          if (!prizes.some(prize => prize.id === prizeId.value)) {
            setPrizeId(null)
          }
        }
      } catch (error) {
        if (error instanceof InstagramInstantwinPrizesApiError) {
          setError(error.type as ErrorCode, error.message)
        }
      }
    }

    const initialize = async (id: number): Promise<boolean> => {
      reset()
      loading.value = true

      const campaignLoaded = await fetchCampaign(id)
      if (!campaignLoaded) {
        loading.value = false
        return false
      }

      await fetchPrizes()

      const analyticsLoaded = await fetchAnalytics()
      if (!analyticsLoaded) {
        loading.value = false
        return false
      }

      loading.value = false
      return true
    }

    const refreshAnalytics = async (): Promise<boolean> => {
      loading.value = true
      const result = await fetchAnalytics()
      loading.value = false
      return result
    }

    const refreshEnquete = async (): Promise<boolean> => {
      return fetchEnqueteAnalytics()
    }

    const refreshMessageSendCounts = async (): Promise<boolean> => {
      const result = await fetchMessageSendCounts(false)
      return Boolean(result)
    }

    const exportMessageSendCountsCsv = async () => {
      return fetchMessageSendCounts(true)
    }

    const updatePublicFlags = async (): Promise<boolean> => {
      if (!campaignId.value) {
        setError('UNKNOWN', 'campaignId is required')
        return false
      }

      try {
        const result = await putCampaignPublicFlags({
          campaignId: campaignId.value,
          isPublicAnalyticsPage: isPublicAnalyticsPage.value,
          isPublicAnalyticsApi: isPublicAnalyticsApi.value
        })
        setError(null)
        return result
      } catch (error) {
        if (error instanceof PutCampaignPublicFlagsApiError) {
          setError(error.type as ErrorCode, error.message)
        } else {
          setError('UNKNOWN', '公開フラグの更新に失敗しました。')
        }
        return false
      }
    }

    const setIntervalValue = (value: Interval) => {
      interval.value = value
    }

    const setMetricSelection = (key: 'value1' | 'value2', value: MetricSelection) => {
      if (key === 'value1') {
        value1.value = value
      } else {
        value2.value = value
      }
    }

    const setPrizeId = (value: number | null) => {
      prizeId.value = value
      resetAdditionalAnalytics()
    }

    const setEnqueteSelectedId = (value: number | null) => {
      enqueteSelectedId.value = value
    }

    const setMessageSendCountSortBy = (value: MessageSendCountSortBy | '') => {
      messageSendCountSortBy.value = value
      messageSendCountPagingNo.value = 1
    }

    const setMessageSendCountPaging = (pagingNo: number, limitCount?: number) => {
      messageSendCountPagingNo.value = pagingNo
      if (typeof limitCount === 'number') {
        messageSendCountLimitCount.value = limitCount
      }
    }

    const setBeginAtZero = (value: boolean) => {
      isBeginAtZero.value = value
    }

    return {
      campaignId,
      campaign,
      analyticsRows,
      prizeOptions,
      prizeId,
      enqueteQuestions,
      enqueteSelectedId,
      enqueteAnswers,
      enqueteTotalCount,
      messageSendCountRows,
      messageSendCountTotal,
      messageSendCountPagingNo,
      messageSendCountLimitCount,
      messageSendCountSortBy,
      enqueteLoading,
      messageSendCountLoading,
      dateTimeRange,
      defaultDateTimeRange,
      interval,
      value1,
      value2,
      isBeginAtZero,
      isPublicAnalyticsPage,
      isPublicAnalyticsApi,
      loading,
      errorCode,
      errorMessage,
      campaignMeta,
      analytics,
      labels,
      entryCount,
      applicantCount,
      newApplicantCount,
      winnerCount,
      followerUpDown,
      setDateTimeRange,
      setIntervalValue,
      setMetricSelection,
      setPrizeId,
      setEnqueteSelectedId,
      setMessageSendCountSortBy,
      setMessageSendCountPaging,
      setBeginAtZero,
      initialize,
      refreshAnalytics,
      refreshEnquete,
      refreshMessageSendCounts,
      exportMessageSendCountsCsv,
      updatePublicFlags,
      reset
    }
  }
)
